/**
 * AcceptInvite Page
 *
 * Handles workspace invitation acceptance via token.
 *
 * @module pages/AcceptInvite
 */

import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link, useLocation } from "react-router-dom";
import { Loader2, CheckCircle, XCircle, LogIn } from "lucide-react";
import { acceptInvitation, validateInvitationToken } from "@/services/workspace";
import { useAuth } from "@/contexts/AuthContext";
import { START_PAGE } from "@/config/startPage";
import { logError } from '../services/logger';
import { useTranslation } from "react-i18next";

type AcceptState = "loading" | "success" | "error" | "invalid" | "signin-required";

/**
 * Where to come back to after signing in.
 *
 * An invitee is by definition someone who may not have an account yet, so the
 * signed-out case is the normal one, not the edge case. The invite link used to
 * hit App's session gate and be replaced with /auth/signin — and a react-router
 * redirect keeps no search string, so the token was gone before this page ran.
 * Signin reads this key on success.
 */
export const INVITE_RETURN_KEY = "ydm_invite_return";

/**
 * AcceptInvite Page Component
 */
const AcceptInvite: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { session, loading } = useAuth();
  /**
   * Accepting is a one-shot side effect and is NOT idempotent: the second call
   * hits the unique constraint on workspace_members and comes back 409, which
   * would land as "Failed to Accept Invitation" on a user who had just been added
   * successfully. Observed live — StrictMode double-invokes in dev, and in
   * production this effect also re-runs whenever the session object changes,
   * which a token refresh does.
   */
  const acceptedToken = useRef<string | null>(null);
  const [state, setState] = useState<AcceptState>("loading");
  const [message, setMessage] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");

  useEffect(() => {
    if (loading) return;

    const token = searchParams.get("token");

    if (!token || !validateInvitationToken(token)) {
      setState("invalid");
      setMessage(t("workspace.inviteLinkInvalid"));
      return;
    }

    // acceptInvitation already returns "Please sign in to accept the invitation",
    // but that branch was unreachable: the router bounced the visitor before the
    // call could be made. Ask here instead, and keep the link so signing in
    // returns to it rather than dropping the invitation on the floor.
    if (!session) {
      try {
        sessionStorage.setItem(INVITE_RETURN_KEY, `${location.pathname}${location.search}`);
      } catch {
        // Private-browsing or a storage quota: the sign-in link still works, the
        // user just has to click the invite link again afterwards.
      }
      setState("signin-required");
      setMessage(t("workspace.inviteSignInPrompt"));
      return;
    }

    if (acceptedToken.current === token) return;
    acceptedToken.current = token;

    const handleAcceptInvitation = async () => {
      try {
        const result = await acceptInvitation(token, t);

        if (result.success && result.data) {
          setState("success");
          setWorkspaceName(result.data.workspaceName);
          setMessage(
            t("workspace.joinedSuccess", { workspace: result.data.workspaceName })
          );

          // Redirect to the start page after 3 seconds
          setTimeout(() => {
            navigate(START_PAGE, { replace: true });
          }, 3000);
        } else {
          setState("error");
          setMessage(result.error || t("workspace.inviteAcceptFailed"));
        }
      } catch (err) {
        setState("error");
        setMessage(t("workspace.inviteUnexpected"));
        logError('AcceptInvite', 'Error accepting invitation', err);
      }
    };

    handleAcceptInvitation();
  }, [searchParams, navigate, session, loading, location.pathname, location.search, t]);

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-sm border border-neutral-200 p-8 text-center">
        {state === "loading" && (
          <>
            <Loader2 className="w-12 h-12 text-ink-900 animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-ink-900 mb-2">
              {t("workspace.inviteAccepting")}
            </h1>
            <p className="text-neutral-700">
              {t("workspace.invitePleaseWait")}
            </p>
          </>
        )}

        {state === "success" && (
          <>
            <CheckCircle className="w-12 h-12 text-green-700 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-ink-900 mb-2">
              {t("workspace.inviteWelcome", { workspace: workspaceName })}
            </h1>
            <p className="text-neutral-700 mb-4">{message}</p>
            <p className="text-sm text-neutral-700">
              {t("workspace.inviteRedirecting")}
            </p>
          </>
        )}

        {state === "error" && (
          <>
            <XCircle className="w-12 h-12 text-red-700 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-ink-900 mb-2">
              {t("workspace.inviteFailed")}
            </h1>
            <p className="text-neutral-700 mb-4">{message}</p>
            <button
              onClick={() => navigate(START_PAGE)}
              className="px-4 py-2 bg-primary-600 text-ink-950 rounded-md hover:bg-primary-700 transition-colors"
            >
              {t("workspace.inviteGoDashboard")}
            </button>
          </>
        )}

        {state === "signin-required" && (
          <>
            <LogIn className="w-12 h-12 text-ink-900 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-ink-900 mb-2">
              {t("workspace.inviteSigninTitle")}
            </h1>
            <p className="text-neutral-700 mb-4">{message}</p>
            <Link
              to="/auth/signin"
              className="inline-block px-4 py-2 bg-primary-600 text-ink-950 rounded-md hover:bg-primary-700 transition-colors"
            >
              {t("workspace.inviteGoSignin")}
            </Link>
          </>
        )}

        {state === "invalid" && (
          <>
            <XCircle className="w-12 h-12 text-yellow-700 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-ink-900 mb-2">
              {t("workspace.inviteInvalid")}
            </h1>
            <p className="text-neutral-700 mb-4">{message}</p>
            <button
              onClick={() => navigate(START_PAGE)}
              className="px-4 py-2 bg-primary-600 text-ink-950 rounded-md hover:bg-primary-700 transition-colors"
            >
              {t("workspace.inviteGoDashboard")}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AcceptInvite;
